import React, { useCallback, useEffect, useRef } from 'react';

export const SaveCancelBar = ({
  onCancel,
  onSave,
  cancelLabel = 'Cancelar',
  saveLabel = 'Salvar',
  saving = false,

  disabledCancel = false,
  disabledSave = false,
  cancelButtonType = 'button',
  saveButtonType = 'button',
  cancelClassName = '',
  className = '',
  enableHotkeys = true,
  enableAutofocus = true,
  // Botão extra opcional entre Cancelar e Salvar — ex.: "Salvar" (fica
  // aberto) ao lado de "Salvar e Fechar" (comportamento padrão). Só
  // funciona com saveButtonType="submit": os dois botões viram
  // <button type="submit"> distintos por `name`/`value`, e quem trata o
  // onSubmit do <form> lê `e.nativeEvent.submitter.value` pra saber qual
  // foi clicado — nenhum consumidor existente precisa mudar nada, pois
  // sem essa prop o comportamento é idêntico a antes (24/08/2026).
  extraSaveButton = null,
}) => {
  const rootRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const saveButtonRef = useRef(null);

  const resolveScope = useCallback(() => {
    const root = rootRef.current;
    if (!root) return null;
    return root.closest('.fixed') || null;
  }, []);

  useEffect(() => {
    if (!enableHotkeys) return;

    const onKeyDownCapture = (e) => {
      if (e.defaultPrevented) return;
      if (e.key !== 'Escape' && e.key !== 'Enter') return;

      const scope = resolveScope();
      if (!scope) return;

      const active = document.activeElement;
      if (active && active !== document.body && active !== document.documentElement && !scope.contains(active)) return;

      if (e.key === 'Escape') {
        if (typeof onCancel !== 'function') return;
        if (disabledCancel || saving) return;
        e.preventDefault();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        onCancel();
        return;
      }

      if (e.isComposing) return;
      if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;

      const target = e.target;
      const tagName = String(target?.tagName || '').toUpperCase();
      if (tagName === 'TEXTAREA' || tagName === 'SELECT') return;
      if (target?.isContentEditable) return;
      if (typeof target?.closest === 'function' && target.closest('[contenteditable="true"], textarea')) return;
      if (typeof target?.closest === 'function' && target.closest('[data-scb-ignore-enter="true"]')) return;
      if (typeof target?.closest === 'function' && target.closest('button, a, [role="button"], [role="link"]')) return;

      if (disabledSave || saving) return;

      e.preventDefault();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

      if (saveButtonType === 'submit') {
        const form =
          (typeof target?.closest === 'function' ? target.closest('form') : null) ||
          (typeof scope.querySelector === 'function' ? scope.querySelector('form') : null) ||
          (typeof rootRef.current?.closest === 'function' ? rootRef.current.closest('form') : null);

        if (form && typeof form.requestSubmit === 'function') {
          form.requestSubmit(saveButtonRef.current || undefined);
          return;
        }
      }

      if (typeof onSave === 'function') onSave();
    };

    window.addEventListener('keydown', onKeyDownCapture, true);
    return () => window.removeEventListener('keydown', onKeyDownCapture, true);
  }, [disabledCancel, disabledSave, enableHotkeys, onCancel, onSave, resolveScope, saveButtonType, saving]);

  useEffect(() => {
    if (!enableAutofocus) return;

    const scope = resolveScope();
    if (!scope) return;

    const active = document.activeElement;
    if (active && scope.contains(active)) return;

    const t = setTimeout(() => {
      const root = rootRef.current;
      if (!root) return;

      const focusables = scope.querySelectorAll(
        'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"], [tabindex]:not([tabindex="-1"])'
      );

      for (const el of focusables) {
        if (!el) continue;
        if (root.contains(el)) continue;
        if (typeof el.focus !== 'function') continue;
        el.focus();
        return;
      }

      if (saveButtonRef.current && typeof saveButtonRef.current.focus === 'function') {
        saveButtonRef.current.focus();
        return;
      }

      if (cancelButtonRef.current && typeof cancelButtonRef.current.focus === 'function') {
        cancelButtonRef.current.focus();
      }
    }, 0);

    return () => clearTimeout(t);
  }, [enableAutofocus, resolveScope]);

  return (
    <div ref={rootRef} className={`flex items-center justify-end gap-2 ${className}`.trim()}>
      <button
        ref={cancelButtonRef}
        type={cancelButtonType}
        onClick={onCancel}
        disabled={disabledCancel || saving}
        className={cancelClassName || `px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 transition-transform active:scale-95 border shadow-sm ${
          disabledCancel || saving
            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
            : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:shadow-md'
        }`}
      >
        <i className="fas fa-times"></i> {cancelLabel}
      </button>
      {extraSaveButton && saveButtonType === 'submit' ? (
        <button
          type="submit"
          name="intent"
          value={extraSaveButton.value}
          disabled={disabledSave || saving}
          className={extraSaveButton.className || `px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 transition-transform active:scale-95 border shadow-sm ${
            disabledSave || saving
              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
              : 'bg-white text-[#20243B] hover:bg-slate-50 border-[#20243B] hover:shadow-md'
          }`}
        >
          <i className={saving ? 'fas fa-spinner fa-spin' : 'fas fa-save'}></i> {extraSaveButton.label}
        </button>
      ) : null}
      <button
        ref={saveButtonRef}
        type={saveButtonType}
        onClick={saveButtonType === 'button' ? onSave : undefined}
        disabled={disabledSave || saving}
        className={`bg-[#20243B] text-white px-6 py-2.5 rounded-lg font-bold hover:bg-[#1a1e30] border border-[#20243B] shadow-md hover:shadow-lg flex items-center gap-2 transition-transform active:scale-95 ${
          disabledSave || saving ? 'opacity-60 cursor-not-allowed' : ''
        }`}
      >
        <i className={saving ? 'fas fa-spinner fa-spin' : 'fas fa-save'}></i> {saveLabel}
      </button>
    </div>
  );
};